import { Injectable } from '@angular/core';
import { Resolve, Router } from '@angular/router';
import { Observable, combineLatest } from 'rxjs';
import { tap, filter, take, timeout, catchError } from 'rxjs/operators';
import { CurrentUserService } from '@macs-frontend/core/services/current-user.service';
import { getUserPermissionsMap } from '@macs-frontend/reducers/meta';
import { CurrentCompanyService } from '@macs-frontend/core/services/current-company.service';
import { emptyCompany } from '@macs-frontend/features/shared/models/company.model';
import { KeycloakService } from '../auth/services/keycloak.service';

@Injectable({
  providedIn: 'root',
})
export class RootRedirectService implements Resolve<any> {
  constructor(
    private userService: CurrentUserService,
    private companyService: CurrentCompanyService,
    private keycloak: KeycloakService,
    private router: Router,
  ) {
    userService.loadUser();
  }

  resolve(): Observable<any> {
    return combineLatest([
      this.userService.user$,
      this.companyService.currentCompany$,
    ]).pipe(
      filter(
        ([user, company]) => !!user?.uuid && !!company?.uuid && company !== emptyCompany,
      ),
      take(1),
      timeout(10000),
      tap(([user, company]) => {
        // Only pure client users land on the client view. A user who is both an
        // agency (company) user and a client user is treated as an agency user
        // and stays on the agency side.
        if (user.clientUser && !user.companyUser) {
          this.router.navigate(['/clients'], {
            replaceUrl: true,
          });
          return;
        }

        // Agency users who are also client users go straight to the projects
        // dashboard rather than the planner.
        if (user.clientUser && user.companyUser) {
          this.router.navigate(['/company-management/projects/dashboard'], {
            replaceUrl: true,
          });
          return;
        }

        const hasPlannerPermission =
          getUserPermissionsMap(user).INTERFACE_COMPANY_MANAGE_PLANNER;

        if (company.isParent && hasPlannerPermission) {
          this.router.navigate(['/company-management/planner/actions'], {
            replaceUrl: true,
          });
        } else {
          this.router.navigate(['/company-management/projects/dashboard'], {
            replaceUrl: true,
          });
        }
      }),
      catchError(error => {
        console.error('RootRedirectService: Failed to load user/company data', error);
        // Under Keycloak, /auth/login is a dead end: the guard lets a
        // KC-authenticated user through to the LEGACY form, whose json_login
        // checks the DB hash — for KC-managed users that rejects the password
        // they just used ("Invalid credentials" with the correct password).
        // The observed failure mode is a stale KC session whose token claims
        // no longer resolve a user/company; a fresh KC login heals it, so log
        // the session out and let the login-required boot start over.
        if (this.keycloak.enabled && this.keycloak.authenticated) {
          this.keycloak.logout(window.location.origin);
        } else {
          this.router.navigate(['/auth/login'], { replaceUrl: true });
        }
        throw error;
      }),
    );
  }
}
